import React, { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import RunButton from '@ui/button/RunButton';
import Loader from '@app/component/loader/Loader';
import { PromotionCountryOverview, PromotionCountryRun } from '@app/model/promotion/PromotionCountryOverview';
import PromotionCountryService, { PromotionCountryQueryKeys } from '@app/model/promotion/service/PromotionCountryService';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import CountryMatrixTable, { countrySource } from './CountryMatrixTable';
import { countryRunCooldownMessage, formatDay, formatDayYear } from './CountryMatrix.utilities';

const TOAST_TITLE = 'Deals by visitor country';
/** A run covers the next 10 nights (user decision 2026-09-05). */
export const RUN_NIGHTS = 10;

/** The run and its nights, as the subtitle names them: `Run of 4 Sep 26, 11:55 UTC · nights 5 – 14 Sep 26`. */
const runLabel = (run: PromotionCountryRun): string =>
	`Run of ${formatDayYear(run.startedAt.slice(0, 10))}, ${run.startedAt.slice(11, 16)} UTC · nights ${formatDay(run.firstNight)} – ${formatDayYear(run.lastNight)}`;

const CountryMatrix: React.FunctionComponent = () => {
	const { companyHotel } = useSelectedCompanyHotel();
	const toaster = useToaster();
	const queryClient = useQueryClient();
	const observedRequestId = useRef<number | undefined>(undefined);
	const handledRequestId = useRef<number | undefined>(undefined);
	useEffect(() => { observedRequestId.current = undefined; handledRequestId.current = undefined; }, [companyHotel.id]);

	const variables = { companyHotelId: companyHotel.id };
	const overviewQuery = useQuery({
		queryKey: PromotionCountryQueryKeys.overview(companyHotel.id),
		queryFn: () => PromotionCountryService.find(variables),
	});
	const statusQuery = useQuery({
		queryKey: PromotionCountryQueryKeys.runStatus(companyHotel.id),
		queryFn: () => PromotionCountryService.runStatus({ companyHotelId: companyHotel.id }),
		refetchInterval: query => query.state.data?.status === 'IN_PROGRESS' ? 1000 : false,
	});
	const runMutation = useMutation({ mutationFn: PromotionCountryService.triggerRun });

	const status = statusQuery.data;
	const isRunning = status?.status === 'IN_PROGRESS';

	/** The run that this page started ends: refresh the matrix and say so, once per request. */
	useEffect(() => {
		if (!status) return;
		if (status.status === 'IN_PROGRESS') {
			observedRequestId.current = status.requestId ?? undefined;
			return;
		}
		const requestId = observedRequestId.current;
		if (requestId === undefined || handledRequestId.current === requestId) return;
		handledRequestId.current = requestId;
		if (status.failed) toaster.error(TOAST_TITLE, 'The country run failed. The previous result remains available while its data is retained.');
		else toaster.success(TOAST_TITLE, 'The countries were run. The table shows the new rates.');
		queryClient.invalidateQueries({ queryKey: PromotionCountryQueryKeys.all });
	}, [status, queryClient, toaster]);

	const run = () => runMutation.mutate(variables, {
		onSuccess: result => {
			queryClient.setQueryData(PromotionCountryQueryKeys.runStatus(companyHotel.id), result);
			if (result.status === 'COOLDOWN') {
				toaster.error(TOAST_TITLE, countryRunCooldownMessage(result.retryAfterSeconds ?? 1));
			}
		},
		onError: () => toaster.error(TOAST_TITLE, 'The countries could not be run at the moment. Please try again later.'),
	});

	const overview: PromotionCountryOverview | undefined = overviewQuery.data;
	const progress = status?.progressPercentage ?? 0;
	const subtitle = isRunning
		? 'Running: the table updates when all regions are done · the last run stays on screen'
		: overview?.run
			? `${runLabel(overview.run)} · ${overview.hotelsWithCountryRate} of ${overview.rows.length} hotels with a probable country rate`
			: `No run yet · all enabled booking.com regions · the next ${RUN_NIGHTS} nights · one press per hour`;

	return <section className="overflow-hidden rounded-lg bg-white shadow">
		<div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start sm:gap-4">
			<div className="min-w-0 sm:flex-1">
				<h2 className="text-base font-semibold text-gray-900">Deals by visitor country — next {RUN_NIGHTS} nights</h2>
				<p className="mt-1 text-xs text-gray-400">{subtitle}</p>
			</div>
			<div className="flex flex-wrap items-center gap-2 sm:shrink-0">
				<RunButton label="Run countries" onClick={run}
					phase={isRunning || runMutation.isPending ? 'running' : 'idle'}
					progress={isRunning ? progress : undefined}
					disabled={isRunning || runMutation.isPending || statusQuery.isLoading || statusQuery.isError} />
			</div>
		</div>

		{overviewQuery.isLoading && <div className="border-t border-gray-300 px-5 py-12"><Loader /></div>}

		{(overviewQuery.isError || statusQuery.isError) &&
			<div className="border-t border-gray-300 bg-red-50 px-5 py-4 text-sm text-red-800">
				The countries could not be loaded. Please try again.
			</div>}

		{overview && !overview.run &&
			<div className="border-t border-gray-300 px-5 py-14 text-center">
				<p className="text-sm text-gray-700">No retained country-run data.</p>
				<p className="mx-auto mt-1.5 max-w-xl text-sm text-gray-500">
					Press Run countries to compare the rates that visitors from all enabled booking.com regions see, for the next {RUN_NIGHTS} nights.
					The run takes about a minute and counts as one refresh of the hotel. Only one run per hour.
				</p>
			</div>}

		{overview?.run && <CountryMatrixTable overview={overview} />}

		<div className="px-5 pb-4">
			<p className="mt-2 text-xs leading-4 text-gray-400">{countrySource(companyHotel.settings.main.currency, overview?.baseRegion ?? companyHotel.settings.main.region)}</p>
		</div>
	</section>;
};

export default CountryMatrix;
